import {
  collection,
  getDocs,
  getDoc,
  setDoc,
  addDoc,
  updateDoc,
  doc,
  deleteDoc,
  arrayUnion,
} from 'firebase/firestore';
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import { db, storage } from '../firebase/firebase';

// ── Storage helpers ──────────────────────────────────────────────

export async function uploadCategoryImage(file: File, catId: string, slot: string): Promise<string> {
  const path = `categories/${catId}_${slot}_${file.name}`;
  const imgRef = storageRef(storage, path);
  await uploadBytes(imgRef, file);
  return getDownloadURL(imgRef);
}

export async function deleteCategoryStorageUrl(url: string): Promise<void> {
  if (!url || !url.includes('firebasestorage')) return;
  try {
    const match = url.match(/\/o\/(.+?)\?/);
    if (!match) return;
    await deleteObject(storageRef(storage, decodeURIComponent(match[1])));
  } catch (e) {
    console.warn('Storage delete skipped:', e);
  }
}

// Firestore collection name
const CATEGORY_COLLECTION = 'Categories';

// Interfaces reflecting your data model
export interface SubCategoryItem {
  id: string;
  image?: string;
  tittle: string;
}

export interface CategoryItem {
  id: string;
  tittle: string;
  subCategoryItems: SubCategoryItem[];
  colorHex?: string; // Optional color for UI
}

export interface CategoryModel {
  id: string;
  tittle: string;
  image?: string;
  colorHex?: string;
  bannerImages: string[];
  categoryItems: CategoryItem[];
}

// ============== Main CRUD for Category ===============

export async function fetchCategories(): Promise<CategoryModel[]> {
  const colRef = collection(db, CATEGORY_COLLECTION);
  const snapshot = await getDocs(colRef);
  return snapshot.docs.map(doc => ({ id: doc.id, ...(doc.data() as any) }));
}

export async function fetchCategory(id: string): Promise<CategoryModel | null> {
  const ref = doc(db, CATEGORY_COLLECTION, id);
  const snapshot = await getDoc(ref);
  return snapshot.exists() ? { id: snapshot.id, ...(snapshot.data() as any) } : null;
}

export async function createOrUpdateCategory(
  category: Omit<CategoryModel, 'id'> & { id?: string }
): Promise<string> {
  if (category.id) {
    const ref = doc(db, CATEGORY_COLLECTION, category.id);
    const { id, ...data } = category;
    await setDoc(ref, data, { merge: true });
    return category.id;
  } else {
    const docRef = await addDoc(collection(db, CATEGORY_COLLECTION), {
      ...category,
      categoryItems: category.categoryItems || [],
    });
    return docRef.id;
  }
}

export async function updateCategory(
  id: string,
  data: Partial<CategoryModel>
): Promise<void> {
  const ref = doc(db, CATEGORY_COLLECTION, id);
  await updateDoc(ref, data);
}

export async function deleteCategory(id: string): Promise<void> {
  await deleteDoc(doc(db, CATEGORY_COLLECTION, id));
}

// ================ Category Item CRUD =============

export async function addCategoryItem(
  categoryId: string,
  newCategoryItem: CategoryItem
): Promise<void> {
  const category = await fetchCategory(categoryId);
  if (!category) throw new Error("Category not found");

  const updatedItems = [...(category.categoryItems || []), newCategoryItem];
  await updateCategory(categoryId, { categoryItems: updatedItems });
}

export async function updateCategoryItem(
  categoryId: string,
  categoryItemId: string,
  updatedData: Partial<CategoryItem>
): Promise<void> {
  const category = await fetchCategory(categoryId);
  if (!category) throw new Error("Category not found");

  const updatedItems = category.categoryItems.map(item =>
    item.id === categoryItemId ? { ...item, ...updatedData } : item
  );

  await updateCategory(categoryId, { categoryItems: updatedItems });
}

export async function deleteCategoryItem(
  categoryId: string,
  categoryItemId: string
): Promise<void> {
  const category = await fetchCategory(categoryId);
  if (!category) throw new Error("Category not found");

  const updatedItems = category.categoryItems.filter(item => item.id !== categoryItemId);
  await updateCategory(categoryId, { categoryItems: updatedItems });
}

// ================ Subcategory CRUD ===============

export async function addSubCategory(
  categoryId: string,
  categoryItemId: string,
  newSubCategory: SubCategoryItem
): Promise<void> {
  const category = await fetchCategory(categoryId);
  if (!category) throw new Error("Category not found");

  const updatedItems = category.categoryItems.map(item => {
    if (item.id === categoryItemId) {
      const newSubs = [...(item.subCategoryItems || []), newSubCategory];
      return { ...item, subCategoryItems: newSubs };
    }
    return item;
  });

  await updateCategory(categoryId, { categoryItems: updatedItems });
}

export async function updateSubCategory(
  categoryId: string,
  categoryItemId: string,
  subCategoryId: string,
  updatedData: Partial<SubCategoryItem>
): Promise<void> {
  const category = await fetchCategory(categoryId);
  if (!category) throw new Error("Category not found");

  const updatedItems = category.categoryItems.map(item => {
    if (item.id === categoryItemId) {
      const updatedSubs = item.subCategoryItems.map(sub =>
        sub.id === subCategoryId ? { ...sub, ...updatedData } : sub
      );
      return { ...item, subCategoryItems: updatedSubs };
    }
    return item;
  });

  await updateCategory(categoryId, { categoryItems: updatedItems });
}

export async function deleteSubCategory(
  categoryId: string,
  categoryItemId: string,
  subCategoryId: string
): Promise<void> {
  const category = await fetchCategory(categoryId);
  if (!category) throw new Error("Category not found");

  const updatedItems = category.categoryItems.map(item => {
    if (item.id === categoryItemId) {
      const filteredSubs = item.subCategoryItems.filter(sub => sub.id !== subCategoryId);
      return { ...item, subCategoryItems: filteredSubs };
    }
    return item;
  });

  await updateCategory(categoryId, { categoryItems: updatedItems });
}

// ================ Banner Image Utilities ===============

export async function addBannerImage(
  categoryId: string,
  imageUrl: string
): Promise<void> {
  const ref = doc(db, CATEGORY_COLLECTION, categoryId);
  await updateDoc(ref, { bannerImages: arrayUnion(imageUrl) });
}

export async function removeBannerImage(
  categoryId: string,
  imageUrl: string
): Promise<void> {
  const category = await fetchCategory(categoryId);
  if (!category) throw new Error("Category not found");

  const filteredBanners = category.bannerImages.filter(img => img !== imageUrl);
  await updateCategory(categoryId, { bannerImages: filteredBanners });
}
